import {
    Box,
    SxProps,
    Theme,
    Typography,
    CircularProgress,
    Dialog,
    DialogTitle,
    IconButton
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useCallback, useRef, useState } from 'react';
import { get, post } from '../../../lib/api';
import { CaseClinicals, CasePolicies } from '../../../lib/models/CaseClinicalPolicies';
import t from '../../../theme';
import { toast } from 'react-toastify';
import { useScreenSmallerThan } from '../../../lib/hooks/breakpoint-hooks';
import ResizableDraggableModal from '../../Utils/ResizableDraggableModal';
import PdfViewer from '../../Utils/PdfViewer';
import { AxiosError } from 'axios';
import { LoadingButton } from '@mui/lab';

type DocumentParams = {
    policyId: string;
    crmCaseId: string;
    clinicalReviewId: string;
    fileName: string;
    type: string;
};

interface FileDataProps {
    fileName: string;
    fileType: string;
    link: string | null;
    currentTabValue: string;
    policyId: string | null;
    crmId: string;
    crmCaseId: string;
    type: string;
    onOpen?: () => void;
    onClose?: () => void;
    updateDocument?: (documentName: string | null) => void;
    clinicalReviewId: string;
}

interface FilesCacheType {
    id: string;
    blobUrl: string;
}

const ViewFileModal = ({
    fileName,
    link,
    policyId,
    crmId,
    crmCaseId,
    type,
    onOpen,
    onClose,
    updateDocument,
    clinicalReviewId
}: FileDataProps) => {
    const [resolved, setResolved] = useState(true);
    const [isDialogOpened, setDialogOpened] = useState(false);
    const [isMobileFileDialogOpen, setIsMobileFileDialogOpen] = useState(false);

    const [fileBlobUrl, setFileBlobUrl] = useState<string>('');

    const filesCache = useRef<Array<FilesCacheType>>([]);

    const boxRef = useRef<HTMLEmbedElement>(null);

    const isSmallerThanLarge = useScreenSmallerThan('lg');
    const isSmallerThanSmall = useScreenSmallerThan('sm');

    const handleCloseDialog = (
        event: unknown,
        reason: 'backdropClick' | 'escapeKeyDown' | 'closeIconClick'
    ) => {
        if (reason !== 'backdropClick') setDialogOpened(false);
        onClose && onClose();
    };

    const handleCloseMobileFileDialog = () => {
        setIsMobileFileDialogOpen(false);
    };

    const modalResizeHandler = (data: DOMRectReadOnly) => {
        const box = boxRef.current;
        if (!box) return;

        box.style.height = `${data.height - 50}px`;
        box.style.width = '100%';
    };

    const getCachedPolicy = (id: string | null) => {
        return filesCache.current.find((f) => f.id === id);
    };

    const markFileAsOpened = useCallback(async () => {
        const document: DocumentParams = {
            policyId: policyId ? policyId : '',
            crmCaseId: crmCaseId,
            clinicalReviewId: clinicalReviewId,
            fileName: fileName,
            type: type
        };

        await post(`casedetails/markfileasopened`, document);
        if (updateDocument) updateDocument(fileName);
    }, [policyId, crmCaseId, updateDocument, fileName, clinicalReviewId, type]);

    const getFileContent = useCallback(async () => {
        const cachedFile = getCachedPolicy(policyId);

        if (cachedFile) {
            setFileBlobUrl(cachedFile.blobUrl);
            if (updateDocument) updateDocument(fileName);
            return true;
        } else {
            try {
                const result = await get(
                    `casedetails/getcasepolicydocument?policyId=${policyId}&crmId=${crmId}&crmCaseId=${crmCaseId}`,
                    { responseType: 'blob' }
                );

                if (result.status === 404) {
                    toast.error('File not found.');
                    return false;
                }

                if (result.data && result.data.size === 0) {
                    toast.error('File is empty.');
                    return false;
                }

                if (result.data) {
                    const blobUrl = URL.createObjectURL(result.data);

                    filesCache.current.push({ id: policyId || '', blobUrl });
                    setFileBlobUrl(blobUrl);
                    return true;
                }

                return false;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (error: any) {
                const axiosError = error as AxiosError;
                const response = axiosError?.response;

                if (response?.status === 400 || response?.status === 404) {
                    const json = JSON.parse(await (response.data as Blob).text());
                    toast.error(json.message);
                    return false;
                }

                console.error('Error fetching file content:', error);
                toast.error('An error has occured.');
                return false;
            }
        }
    }, [policyId, updateDocument, fileName, crmId, crmCaseId]);

    const handleOpenDialog = useCallback(() => {
        if (type === CasePolicies) {
            setResolved(false);
            getFileContent()
                .then((isFileSuccess) => {
                    if (isFileSuccess) {
                        if (isSmallerThanLarge) {
                            setIsMobileFileDialogOpen(true);
                        } else {
                            setDialogOpened(true);
                        }
                        markFileAsOpened();
                    }
                })
                .finally(() => setResolved(true));
        } else {
            if (!link && !fileBlobUrl) {
                toast.error('Case policy document does not exist!');
            } else {
                window.open(link || '', '_blank');
                markFileAsOpened();
            }
        }
    }, [link, fileBlobUrl, getFileContent, markFileAsOpened, type, isSmallerThanLarge]);

    return (
        <Box>
            <LoadingButton
                loading={!resolved}
                sx={buttonStyle(isSmallerThanSmall)}
                onClick={handleOpenDialog}>
                View
            </LoadingButton>
            {isSmallerThanLarge ? (
                <Dialog
                    fullScreen={true}
                    open={isMobileFileDialogOpen}
                    onClose={handleCloseMobileFileDialog}>
                    <DialogTitle>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                            <Typography>{fileName}</Typography>
                            <IconButton
                                edge="start"
                                color="inherit"
                                onClick={handleCloseMobileFileDialog}
                                aria-label="close">
                                <Close />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    {link === null && !fileBlobUrl && (
                        <Typography sx={textStyle}>
                            <CircularProgress size={'4rem'} sx={{ color: 'secondary.main' }} />
                        </Typography>
                    )}

                    {type === CaseClinicals && link !== null && (
                        <iframe title={fileName} src={link} height="100%" width="100%"></iframe>
                    )}
                    {type === CasePolicies && fileBlobUrl && (
                        <PdfViewer fileName={fileName} url={fileBlobUrl} />
                    )}
                </Dialog>
            ) : (
                <ResizableDraggableModal
                    size={{ width: 680, height: 900 }}
                    title={<Typography sx={{ wordBreak: 'break-word' }}>{fileName}</Typography>}
                    open={isDialogOpened}
                    onClose={handleCloseDialog}
                    onResize={modalResizeHandler}
                    onOpen={onOpen}>
                    <Box
                        ref={boxRef}
                        sx={{
                            height: '800px',
                            width: isSmallerThanLarge ? '600px' : '640px',
                            padding: '12px'
                        }}>
                        <Box
                            sx={{
                                height: '100%',
                                width: '100%',
                                background: t.palette.cardSubtitle.main
                            }}>
                            {link === null && !fileBlobUrl && (
                                <Typography sx={textStyle}>
                                    <CircularProgress
                                        size={'4rem'}
                                        sx={{ color: 'secondary.main' }}
                                    />
                                </Typography>
                            )}

                            {type === CaseClinicals && link !== null && (
                                <iframe
                                    title={fileName}
                                    src={link}
                                    height="100%"
                                    width="100%"></iframe>
                            )}
                            {type === CasePolicies && fileBlobUrl && (
                                <PdfViewer fileName={fileName} url={fileBlobUrl} />
                            )}
                        </Box>
                    </Box>
                </ResizableDraggableModal>
            )}
        </Box>
    );
};

const buttonStyle: (isSmallerThanSmall: boolean) => SxProps<Theme> =
    (isSmallerThanSmall) => (t: Theme) => ({
        textDecoration: 'underline',
        fontSize: isSmallerThanSmall ? '13px' : '16px',
        '&:hover': {
            backgroundColor: 'none',
            textDecoration: 'underline',
            '& .MuiListItemIcon-root': {
                textDecoration: 'underline',
                background: 'none'
            }
        }
    });

const textStyle: SxProps<Theme> = (t: Theme) => ({
    display: 'flex',
    fontSize: '64px',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center'
});

export default ViewFileModal;
